import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft } from "lucide-react";

export default function ResetPasswordPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const initialEmail = sp.get("email") || "";
  const { setSession } = useAuth();
  const [step, setStep] = useState(initialEmail ? "verify" : "request");
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const request = async () => {
    setError(null); setInfo(null);
    if (!email.trim()) return setError("Please enter your email");
    setLoading(true);
    try {
      const r = await apiFetch("/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.detail || "Could not start reset");
      }
      const data = await r.json().catch(() => ({}));
      if (data.dev_otp) {
        setCode(data.dev_otp);
        setInfo(`(Preview) OTP auto-filled: ${data.dev_otp}`);
      } else {
        setInfo("If that account exists we've emailed a 6-digit reset code.");
      }
      setStep("verify");
    } catch (err) {
      setError(err.message || "Could not start reset");
    } finally { setLoading(false); }
  };

  const verify = async () => {
    setError(null);
    if (code.trim().length !== 6) return setError("Enter the 6-digit code");
    if (newPassword.length < 6) return setError("Password must be at least 6 characters");
    setLoading(true);
    try {
      const r = await apiFetch("/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          code: code.trim(),
          new_password: newPassword,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Reset failed");
      await setSession(data.token, data.user);
      nav("/events", { replace: true });
    } catch (err) {
      setError(err.message || "Reset failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-primary mb-4" data-testid="reset-back">
          <ChevronLeft size={16} /> Back
        </Link>
        <Card>
          <CardContent className="p-6 space-y-4">
            <h1 className="text-2xl font-semibold">
              {step === "request" ? "Reset your password" : "Enter reset code"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {step === "request"
                ? "We'll email you a 6-digit code to set a new password."
                : `We sent a code to ${email}. Enter it below and choose a new password.`}
            </p>
            {step === "request" ? (
              <>
                <div className="space-y-1">
                  <Label>Email</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="reset-email" />
                </div>
                {error && <div className="text-sm text-destructive" data-testid="reset-error">{error}</div>}
                {info && <div className="text-sm text-emerald-700">{info}</div>}
                <Button onClick={request} disabled={loading} data-testid="reset-request" className="w-full">
                  {loading ? "Sending…" : "Send reset code"}
                </Button>
              </>
            ) : (
              <>
                <div className="space-y-1">
                  <Label>6-digit code</Label>
                  <Input inputMode="numeric" maxLength={6} value={code}
                         onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                         placeholder="123456" className="text-center text-2xl tracking-[10px]"
                         data-testid="reset-code" />
                </div>
                <div className="space-y-1">
                  <Label>New password</Label>
                  <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                         placeholder="At least 6 characters" data-testid="reset-newpass" />
                </div>
                {error && <div className="text-sm text-destructive" data-testid="reset-error">{error}</div>}
                <Button onClick={verify} disabled={loading} data-testid="reset-verify" className="w-full">
                  {loading ? "Please wait…" : "Set new password & sign in"}
                </Button>
                <Button variant="ghost" onClick={request} data-testid="reset-resend" className="w-full">
                  Resend code
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
